import { useEffect, useRef, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function CustomerChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(localStorage.getItem("gf_chat_session") || (() => {
    const id = `chat-${Date.now()}`;
    localStorage.setItem("gf_chat_session", id);
    return id;
  })()).current;
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/ai/chat/history/${sessionId}`).then((r) => setMessages(r.data));
  }, [sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async () => {
    const t = input.trim();
    if (!t || loading) return;
    setInput("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", text: t }]);
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { session_id: sessionId, message: t });
      setMessages((m) => [...m, { id: `r-${Date.now()}`, role: "assistant", text: r.data.reply }]);
    } catch (_) { toast.error("Chat failed"); }
    setLoading(false);
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-3xl mb-6 p-6 text-white"
        style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 60%, #7A1010 100%)" }}>
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#F2C97A]/30 blur-3xl" />
        <div className="absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-[#3F6B3F]/20 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur border border-white/25 grid place-items-center">
            <Sparkles className="w-7 h-7 text-[#F2C97A]" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-white/70 font-bold">AI Assistant</div>
            <h1 className="font-display text-2xl md:text-3xl">EarnBot</h1>
            <p className="text-xs text-white/80">Your 24×7 helper for KYC, lots & sales</p>
          </div>
        </div>
      </div>

      <div className="gf-card p-5 min-h-[60vh] flex flex-col">
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {messages.length === 0 && (
            <div className="text-sm text-[#6B5F58] py-8 text-center">
              Ask anything about KYC, lots, lock-in, sales — I'll explain plainly.
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                m.role === "user" ? "bg-[#D71921] text-white rounded-br-sm" : "bg-[#F8F4E9] text-[#1F1816] rounded-bl-sm border border-[#E8E0D2]"
              }`}>{m.text}</div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#F8F4E9] border border-[#E8E0D2] px-4 py-2.5 rounded-2xl text-sm">
                <span className="inline-block w-2 h-2 rounded-full bg-[#D71921] mr-1 animate-pulse" />
                <span className="inline-block w-2 h-2 rounded-full bg-[#D71921] mr-1 animate-pulse" style={{ animationDelay: "0.15s" }} />
                <span className="inline-block w-2 h-2 rounded-full bg-[#D71921] animate-pulse" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="mt-4 flex gap-2">
          <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type your question…"
            className="flex-1 rounded-xl border border-[#E8E0D2] px-4 py-3 outline-none focus:border-[#D71921]" />
          <button onClick={send} disabled={loading} data-testid="chat-send-btn"
            className="px-4 rounded-xl bg-[#D71921] text-white disabled:opacity-60">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
